import {ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, Host, input, Input, Optional, Self, signal, ViewChild} from '@angular/core';
import {AbstractControl, FormsModule, NgControl, ReactiveFormsModule, ValidationErrors, ValidatorFn} from '@angular/forms';
import {MatAutocomplete, MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatFormField, MatFormFieldControl} from '@angular/material/form-field';
import {filtrerTilFørsteInnslag, første, mapBasertPåIndeks, nyArray} from "felles-ng2/støttefunksjoner/lister";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Temporal} from "temporal-polyfill";
import {MatOption} from "@angular/material/core";
import {FormaterDurationPipe} from "felles-ng2/pipes/FormaterDurationPipe";
import {durationTilMinutter, erPlainTimeTidligereEnnEllerLik} from "felles-ng2/støttefunksjoner/temporal";
import {BasisKlokkeslettVelgerKomponent} from "../klokkeslett-velger/BasisKlokkeslettVelgerKomponent";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {Skedulering} from "felles-ng2/støttefunksjoner/timeout";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {filter} from "rxjs";
import PlainTime = Temporal.PlainTime;
import Duration = Temporal.Duration;

const debug = initDebugger('VarighetVelgerKomponent');

/**
 * Input-felt for angivelse av varighet som lar bruker velge skrive inn klokkeslett
 * samt velge fra en nedtrekksliste med predefinerte klokkeslett.
 *
 * Dersom starttidspunkt-attributtet er angitt vil nedtrekksliste inneholder klokkeslett som starter fra angitt tidspunkt.
 *
 * Fungerer med Ng template-basert form ([(ngModel)]="...") og Ng reactive form ([formControl]="...").
 * Fungerer med <mat-form-field> fra Material.
 */
@Component({
    selector: 'varighet-velger',
    templateUrl: 'varighet-velger.html',
    styleUrls: ['varighet-velger.scss'],
    imports: [
        FormsModule,
        MatInputModule,
        ReactiveFormsModule,
        MatAutocompleteModule,
        FormaterDurationPipe
    ],
    providers: [{ provide: MatFormFieldControl, useExisting: VarighetVelgerKomponent }],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VarighetVelgerKomponent extends BasisKlokkeslettVelgerKomponent {

    @ViewChild('tekstfelt', {static: false}) private _tekstfelt: ElementRef<HTMLInputElement> | null = null;
    @ViewChild(MatAutocomplete) private _autocomplete: MatAutocomplete | null = null;

    predefinerteVarigheter = computed(() => this.varigheter(this.starttidspunktSignal()))
    formatertVarighet = computed(() => this.beregnVarighet(this.starttidspunktSignal(), this.klokkeslettSomPlainTime()));

    private starttidspunktSignal = signal<PlainTime | null>(null);

    private starttidspunktVedForskyvning: PlainTime | null = null;
    /** ved endring i starttidspunkt opprettes det en timer som forskyver tidspunkt ihht endring i starttidspunkt */
    private skedulertForskyvning: Skedulering;
    /** antall millisekunder mellom endring av klokkeslett og starttidspunkt der vi undertrykker forskyvning */
    static undertrykkForskyvningVindu = 200;

    constructor(/** skjemakontrollen tilknyttet VarighetVelgerKomponent via formControl eller ngModel direktiver*/
                @Self() tilknyttetSkjemakontroll: NgControl,
                /** mulig MatFormField som omslutter denne komponenten */
                @Optional() @Host() matFormField: MatFormField,
                destroyRef: DestroyRef,
                gjeldendeTidssone: GjeldendeTidssone) {
        super(tilknyttetSkjemakontroll, matFormField, destroyRef, gjeldendeTidssone, 'varighet');

        this.klokkeslettTekstfelt.addValidators(this.klokkeslettTekstfeltValidator())

        //for å forhindre forskyvning av klokkeslett når både klokkeslett og starttidspunkt endres tilnærmet samtidig
        //blokkerer vi for forskyvning av klokkeslett en viss periode etter at klokkeslett har blitt endret,
        //med mindre endringen er forårsaket av en forskyvning..
        this.skedulertForskyvning = new Skedulering(VarighetVelgerKomponent.undertrykkForskyvningVindu, 'forskyv klokkeslett', destroyRef);
        this.klokkeslettTekstfelt.valueChanges
          .pipe(
            takeUntilDestroyed(destroyRef),
            filter(() => !this.skedulertForskyvning.erArbeidUtføres())) //ikke registrer ny blokkering dersom endringen er forårsaket av en forskyvning
          .subscribe(() => this.skedulertForskyvning.blokkerSkeduleringer(VarighetVelgerKomponent.undertrykkForskyvningVindu));
    }

    /** tidspunktet beregnet varighet beregnes ut fra, optional.
     * Dersom ikke angitt vises ikke 'varighet' i gui.
     * Dersom starttidspunkt endres vil valgt klokkeslett endres tilsvarende. */
    @Input() set starttidspunkt(starttidspunkt: PlainTime | null) {
        this.debug('starttidspunkt set', starttidspunkt?.toString(), this.skedulertForskyvning.toString())

        //forskyv starttidspunkt i tråd med nytt starttidspunkt, etter en liten forsinkelse slik at vi kan avbryte forskyvning dersom også klokkeslett-skjemaverdien endres
        //i løpet av de neste VarighetVelgerKomponent.undertrykkForskyvningVindu millisekundene
        if (erIkkeNil(this.klokkeslettSomPlainTime()) && erIkkeNil(this.starttidspunktSignal()) && erIkkeNil(starttidspunkt)) {
            if (!this.skedulertForskyvning.erArbeidSkedulert()) this.starttidspunktVedForskyvning = this.starttidspunktSignal();
            this.debug(`skeduler forskyvning pga start ${this.starttidspunktVedForskyvning!.toString()} -> ${starttidspunkt.toString()}`)
            this.skedulertForskyvning.skeduler(() => this.forskyvKlokkeslett(this.starttidspunktVedForskyvning!, starttidspunkt))
        }
        //ideelt sett burde ikke starttidspunktSignal vært oppdatert umiddelbart (gir 'blinking' i visning av relativ varighet i gui),
        //men vært forsinket på samme måte som forskyvKlokkeslett,
        //men i motsetning til forskyvKlokkeslett (som vi ikke ønsker å kjøre dersom både klokkeslett og starttidspunkt blir endret innenfor et tidsvindu)
        //må starttidspunktSignal alltid oppdateres.. kan derfor ikke ligge i samme forsinkede kjøring som starttidspunktSignal
        this.debug(`ubetinget set starttidspunktSignal`, starttidspunkt?.toString())
        this.starttidspunktSignal.set(starttidspunkt);

        //Kjør validering på nytt - valgt klokkeslett kan nå være tidligere enn starttidspunkt
        //updateValueAndValidity({emitEvent: true}) vil føre til at klokkeslettTekstfelt.valueChanges leverer ny verdi,
        //hvorpå skedulertForskyvning.blokkerSkeduleringer kalles og eventuell skedulert forskyvning av klokkeslett blir kanselert,
        //derfor kaller vi med emitEvent:false derom det er skedulert en forskyvning
        //forskyvKlokkeslett() vil indirekte sørge for at validering blir trigget på nytt med emitEvent:true slik at foreldre-skjemakomponent får sin valid-status oppdatert
        this.klokkeslettTekstfelt.updateValueAndValidity({emitEvent: !this.skedulertForskyvning.erArbeidSkedulert()});
    }

    //BasisKlokkeslettVarighetKomponent impl

    override get tekstfelt(): ElementRef<HTMLInputElement> | null {
        return this._tekstfelt;
    }

    override get autocomplete(): MatAutocomplete | null {
        return this._autocomplete;
    }

    /** Finner matchende MatOption innslag fra options-listen til mat-autocomplete komponenten.
     * null dersom intet treff.
     * public for testbarhet */
    override finnNærmesteValgAlternativ(klokkeslett: string): MatOption | null {
        if (erTomEllerNil(klokkeslett)) return null;
        const justertKlokke = klokkeslett.trim();
        const valgAlternativ = første(this.autocomplete!.options
          .filter(kandidat => kandidat.viewValue.toLowerCase().includes(justertKlokke)));
        if (erIkkeNil(valgAlternativ)) return valgAlternativ;

        //intent direkte treff - finn nærmeste innslag ved å søke gjennom predefinerteVarigheter, gitt at angitt klokkeslett er velformatert
        const klokkeslettSomPlainTime = this.strengTilPlainTime(klokkeslett);
        if (erNil(klokkeslettSomPlainTime)) return null;

        const nærmesteValgAlternativ = this.predefinerteVarigheter()
          //finn predefinerteVarigheter innslag med samme time som ligger etter angitt klokkeslett i tid
          .filter(innslag =>
            innslag.klokkeslettSomPlainTime.hour == klokkeslettSomPlainTime.hour &&
            innslag.klokkeslettSomPlainTime.minute >= klokkeslettSomPlainTime.minute)
          .filter(filtrerTilFørsteInnslag()) //snevrer inn til første VarighetModell
          .map(innslag => this.plainTimeTilStreng(innslag.klokkeslettSomPlainTime))
          //finn matchende innslag i options-listen til mat-autocomplete komponenten
          .flatMap(nærmesteKlokkeslett => this.autocomplete!.options
            .filter(kandidat => kandidat.viewValue.toLowerCase().includes(nærmesteKlokkeslett)))
        return første(nærmesteValgAlternativ);
    }

    //private hjelpemetoder

    /** Kalles ved endringer i klokkeslettTekstfelt.
     * Validerer at angitt klokkeslett er senere enn angitt {@link #starttidspunkt} */
    private klokkeslettTekstfeltValidator(): ValidatorFn {
        return (klokkeslettKontroller: AbstractControl<string>): ValidationErrors | null => {
            const slutttidspunkt = this.strengTilPlainTime(klokkeslettKontroller.value);
            this.debug('klokkeslettTekstfeltValidator starttidspunkt: %s slutttidspunkt:%s', this.starttidspunktSignal()?.toString(), slutttidspunkt?.toString() );

            if (erNil(this.starttidspunktSignal()) || erNil(slutttidspunkt)) return null;
            if (!erPlainTimeTidligereEnnEllerLik(slutttidspunkt, this.starttidspunktSignal()!)) return null;

            this.debug('klokkeslettTekstfeltValidator negativVarighet', klokkeslettKontroller.value);
            return {'negativVarighet': true};
        };
    }

    /** Returnerer liste med tidspunkter fra angitt startTidspunkt til midnatt */
    private varigheter(startTidspunkt: PlainTime | null): VarighetModell[] {
        const tidspunkt = startTidspunkt ?? new PlainTime(0, 0);
        const antallMinutterTilMidnatt = durationTilMinutter(tidspunkt.until(new PlainTime(23, 59)))!;

        return [1, 2,
            ...nyArray(12).map(mapBasertPåIndeks(indeks => (indeks + 1) * 5)), //5 min intervaller fra 5 til 60
            ...nyArray(4 * 23).map(mapBasertPåIndeks(indeks => 60 + ((indeks + 1) * 15))), //15 min intervaller fra > 1 time
        ]
          //sørger for at vi ikke runder midnatt
          .filter(minutter => minutter < antallMinutterTilMidnatt)
          .map(minutter => {
              const klokkeslettSomPlainTime = tidspunkt.add({minutes: minutter})
              return {
                  klokkeslettSomPlainTime: klokkeslettSomPlainTime,
                  klokkeslett: this.plainTimeTilStreng(klokkeslettSomPlainTime),
                  varighet: this.beregnVarighet(startTidspunkt, klokkeslettSomPlainTime)!
              }
          })
    }

    private beregnVarighet(startTidspunkt: PlainTime | null, tidspunkt: PlainTime | null): Duration | null {
        if (erNil(startTidspunkt) || erNil(tidspunkt)) return null;
        return startTidspunkt.until(tidspunkt);
    }

    private forskyvKlokkeslett(opprinneligStarttidspunkt: PlainTime, nyttStarttidspunkt: PlainTime) {
        const forskjøvet = this.klokkeslettSomPlainTime()!.add(opprinneligStarttidspunkt.until(nyttStarttidspunkt))
        this.debug(`forskyver klokkeslett fra ${this.klokkeslettTekstfelt.value} til ${this.plainTimeTilStreng(forskjøvet)} pga starttidspunkt endring`)
        this.klokkeslettTekstfelt.setValue(this.plainTimeTilStreng(forskjøvet)); //merk at dette trigger lyttTilKlokkeslettTekstfeltEndringer som i sin tur trigger validering
        //BasisKlokkeslettVelgerKomponent.lyttTilKlokkeslettTekstfeltEndringer kaller kun eksponerEndretKlokkeslett dersom feltet klokkeslettTekstfelt.dirty
        //sørger her for å propagere endring selvom feltet ikke er dirty
        if (!this.klokkeslettTekstfelt.dirty) this.eksponerEndretKlokkeslett(forskjøvet);
    }
}

interface VarighetModell {
    klokkeslettSomPlainTime: PlainTime;
    klokkeslett: string;
    varighet: Duration;
}

